/**
 * The claims the adjudicator refused to settle on its own.
 *
 * The server parks a reward it cannot vouch for in a third state — neither
 * granted nor forfeited — precisely so a person can look. Without this screen
 * that state would just be a slow way of losing someone's credits.
 *
 * It sits under Alarms rather than on a page of its own because ruling on a
 * claim means looking at the alarm behind it first, and the two are one click
 * apart here.
 *
 * A ruled claim leaves the queue at once; the rulings themselves are under
 * History, newest first, with who made each one and why.
 */

import { useState, type ReactNode } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AlarmClock, Check, Gavel, History, ScrollText, X } from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  cn,
  EmptyState,
  ErrorNote,
  IdChip,
  Pagination,
  Skeleton,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDateTime, formatNumber } from '../../lib/format';
import { useT } from '../../lib/i18n';
import { useOpenRecord } from '../../lib/navigation';
import { errorMessage, useToast } from '../../lib/toast';
import type { AdminAlarmEvent, Paginated } from '../../lib/types';
import { EventSummary, RiskBadge } from './AlarmEvents';

type ReviewView = 'pending' | 'history';

const PAGE_SIZE = 25;

export function ReviewQueue() {
  const t = useT();
  const [view, setView] = useState<ReviewView>('pending');
  const pending = view === 'pending';

  return (
    <Card>
      <CardHeader
        title={t(pending ? 'events.review.title' : 'events.review.historyTitle')}
        description={t(
          pending
            ? 'events.review.description'
            : 'events.review.historyDescription',
        )}
        action={<ViewToggle value={view} onChange={setView} />}
      />
      {pending ? <PendingClaims /> : <RulingHistory />}
    </Card>
  );
}

function ViewToggle({
  value,
  onChange,
}: {
  value: ReviewView;
  onChange: (view: ReviewView) => void;
}) {
  const t = useT();
  const views: { id: ReviewView; icon: ReactNode }[] = [
    { id: 'pending', icon: <Gavel size={14} /> },
    { id: 'history', icon: <History size={14} /> },
  ];

  return (
    <div
      role="tablist"
      className="flex shrink-0 gap-1 rounded-lg border border-line bg-raised p-0.5"
    >
      {views.map((view) => (
        <button
          key={view.id}
          type="button"
          role="tab"
          aria-selected={value === view.id}
          onClick={() => onChange(view.id)}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
            value === view.id
              ? 'bg-surface text-ink shadow-sm'
              : 'text-ink-2 hover:text-ink',
          )}
        >
          {view.icon}
          {t(`events.review.view.${view.id}`)}
        </button>
      ))}
    </div>
  );
}

/** Claims nobody has ruled on yet. A ruled claim leaves the list at once. */
function PendingClaims() {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [ruling, setRuling] = useState<{
    event: AdminAlarmEvent;
    approve: boolean;
  } | null>(null);

  const claims = useQuery({
    queryKey: ['review', page],
    queryFn: () =>
      api.get<Paginated<AdminAlarmEvent>>('/api/events/review', {
        page,
        limit: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const resolve = useMutation({
    mutationFn: ({
      eventId,
      approve,
      reason,
    }: {
      eventId: string;
      approve: boolean;
      reason: string;
    }) => api.post(`/api/events/review/${eventId}`, { approve, reason }),
    onSuccess: (_, { eventId }) => {
      toast.success(t('events.review.ruled'));
      setRuling(null);
      // Drop the claim from what is on screen now, rather than leaving it
      // there until the refetch lands.
      const drop = (data: Paginated<AdminAlarmEvent> | undefined) =>
        data && {
          ...data,
          items: data.items.filter((item) => item.id !== eventId),
          total: Math.max(0, data.total - 1),
        };
      queryClient.setQueryData(['review', page], drop);
      queryClient.setQueryData(['review-count'], drop);
      // The last claim on a later page: step back rather than show it empty.
      if (page > 1 && claims.data?.items.length === 1) setPage(page - 1);

      void queryClient.invalidateQueries({ queryKey: ['review'] });
      void queryClient.invalidateQueries({ queryKey: ['review-count'] });
      void queryClient.invalidateQueries({ queryKey: ['review-history'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (claims.isPending) return <Skeleton className="m-4 h-64" />;
  if (claims.isError) {
    return (
      <div className="p-4">
        <ErrorNote error={claims.error} />
      </div>
    );
  }

  const items = claims.data.items;

  return (
    <>
      {items.length === 0 ? (
        <EmptyState
          icon={<Gavel size={28} />}
          title={t('events.review.emptyTitle')}
          description={t('events.review.emptyDescription')}
        />
      ) : (
        <>
          <ul className="divide-y divide-line">
            {items.map((claim) => (
              <ReviewRow
                key={claim.id}
                claim={claim}
                onRule={(approve) => setRuling({ event: claim, approve })}
              />
            ))}
          </ul>
          <Pagination
            page={claims.data.page}
            totalPages={claims.data.totalPages}
            total={claims.data.total}
            onChange={setPage}
          />
        </>
      )}

      <ConfirmDialog
        open={ruling !== null}
        title={t(
          ruling?.approve
            ? 'events.review.approveTitle'
            : 'events.review.rejectTitle',
        )}
        description={t(
          ruling?.approve
            ? 'events.review.approveDescription'
            : 'events.review.rejectDescription',
        )}
        destructive={ruling?.approve === false}
        confirmLabel={t(
          ruling?.approve
            ? 'events.review.approveConfirm'
            : 'events.review.rejectConfirm',
        )}
        loading={resolve.isPending}
        onCancel={() => setRuling(null)}
        onConfirm={(reason) =>
          ruling &&
          resolve.mutate({
            eventId: ruling.event.id,
            approve: ruling.approve,
            reason,
          })
        }
      />
    </>
  );
}

/** Rulings already made, newest first. Fetched only when opened. */
function RulingHistory() {
  const t = useT();
  const [page, setPage] = useState(1);

  const rulings = useQuery({
    queryKey: ['review-history', page],
    queryFn: () =>
      api.get<Paginated<AdminAlarmEvent>>('/api/events/review/history', {
        page,
        limit: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  if (rulings.isPending) return <Skeleton className="m-4 h-64" />;
  if (rulings.isError) {
    return (
      <div className="p-4">
        <ErrorNote error={rulings.error} />
      </div>
    );
  }

  const items = rulings.data.items;
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<History size={28} />}
        title={t('events.review.historyEmptyTitle')}
        description={t('events.review.historyEmptyDescription')}
      />
    );
  }

  return (
    <>
      <ul className="divide-y divide-line">
        {items.map((ruling) => (
          <HistoryRow key={ruling.id} ruling={ruling} />
        ))}
      </ul>
      <Pagination
        page={rulings.data.page}
        totalPages={rulings.data.totalPages}
        total={rulings.data.total}
        onChange={setPage}
      />
    </>
  );
}

/**
 * One claim: who it belongs to, what the rules made of it, the two ways to
 * look further, and the two ways to end it.
 */
function ReviewRow({
  claim,
  onRule,
}: {
  claim: AdminAlarmEvent;
  onRule: (approve: boolean) => void;
}) {
  const t = useT();
  const open = useOpenRecord();
  const { alarmId } = claim;

  return (
    <li className="px-4 py-3.5 sm:px-5">
      <div className="flex flex-wrap items-center gap-2">
        <IdChip id={claim.userId} title={t('common.userId')} />
        {claim.risk && <RiskBadge risk={claim.risk} />}
        <span className="text-xs text-ink-3">
          {formatDateTime(claim.occurredAt)}
        </span>
      </div>

      <p className="mt-1.5">
        <EventSummary event={claim} />
      </p>

      {/* Context first, then the ruling: an operator should be able to reach
          the alarm behind a claim without leaving the claim unresolved. */}
      <div className="mt-2.5 flex flex-wrap justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {alarmId && (
            <Button
              icon={<AlarmClock size={15} />}
              onClick={() => open({ kind: 'alarm', id: alarmId })}
            >
              {t('events.viewAlarm')}
            </Button>
          )}
          <Button
            icon={<ScrollText size={15} />}
            onClick={() => open({ kind: 'event', id: claim.id })}
          >
            {t('events.viewEvent')}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button icon={<Check size={15} />} onClick={() => onRule(true)}>
            {t('events.review.approve')}
          </Button>
          <Button
            variant="danger"
            icon={<X size={15} />}
            onClick={() => onRule(false)}
          >
            {t('events.review.reject')}
          </Button>
        </div>
      </div>
    </li>
  );
}

/** One ruling: the outcome, who it was for, and the way back to the claim. */
function HistoryRow({ ruling }: { ruling: AdminAlarmEvent }) {
  const t = useT();
  const open = useOpenRecord();
  const approved = ruling.type === 'reward_earned';
  const { alarmId, reviewedClaimId } = ruling;

  return (
    <li className="px-4 py-3.5 sm:px-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          tone={approved ? 'good' : 'danger'}
          icon={approved ? <Check size={12} /> : <X size={12} />}
        >
          {t(approved ? 'events.review.approved' : 'events.review.rejected')}
        </Badge>
        <IdChip id={ruling.userId} title={t('common.userId')} />
        {ruling.amount > 0 && (
          <span className="tnum text-xs font-medium text-danger">
            {t('events.review.forfeited', {
              amount: formatNumber(ruling.amount),
            })}
          </span>
        )}
        <span className="text-xs text-ink-3">
          {formatDateTime(ruling.occurredAt)}
        </span>
      </div>

      <p className="mt-1.5">
        <EventSummary event={ruling} />
      </p>

      <div className="mt-2.5 flex flex-wrap gap-2">
        {reviewedClaimId && (
          <Button
            icon={<ScrollText size={15} />}
            onClick={() => open({ kind: 'event', id: reviewedClaimId })}
          >
            {t('events.review.viewClaim')}
          </Button>
        )}
        {alarmId && (
          <Button
            icon={<AlarmClock size={15} />}
            onClick={() => open({ kind: 'alarm', id: alarmId })}
          >
            {t('events.viewAlarm')}
          </Button>
        )}
      </div>
    </li>
  );
}
